import { VFC, useState } from 'react';
import {
  Col,
  Container,
  Row,
  Visible,
  useScreenClass,
} from 'react-grid-system';
import styled from 'styled-components';
import { useReturnContext } from '../../contexts/returnContext';
import { useSettings } from '../../contexts/settingsContext';
import { tUpdateAndAddToReturnArgs } from '../../contexts/zod';
import { theme } from '../../utils/theme';
import Spacing from '../atoms/Spacing';
import TeritaryButton from '../atoms/TertiaryButton';
import Typography from '../atoms/Typography';
import IconButton from '../molecules/IconButton';
import ProductImage from '../molecules/ProductImage';
import TableItem from '../molecules/TableItem';
import OrderLineForm from './form/OrderLineForm';

interface Customization {
  radius?: string;
}

const StyledContainer = styled(Container) <Customization>`
  background-color: #fafafa;
  border: 1px solid #e0e0e0;
  border-radius: ${({ radius }) => radius};
`;

const BorderRow = styled(Container) <Customization>`
  background-color: white;
  border-radius: ${({ radius }) => radius};
  border: 1px solid #e0e0e0;
`;

const FormBorder = styled(Container) <Customization>`
  background-color: white;
  border-radius: ${({ radius }) => radius};
  border: 1px solid #e0e0e0;
`;

const ReturnListItem: VFC<tUpdateAndAddToReturnArgs> = (props) => {
  const { removeFromReturns } = useReturnContext();
  const { cornerRadius } = useSettings();
  const screenClass = useScreenClass();
  const [open, setOpen] = useState(false);

  const {
    imageUrl,
    description,
    variant,
    returnData,
    productNumber,
    variantDescription,
    uid,
  } = props;

  const table = [
    {
      title: <Typography translateKey="art_no" variant="body1-semi-bold" />,
      value: `${productNumber}${variant}`,
    },
    {
      title: (
        <Typography translateKey="product_name" variant="body1-semi-bold" />
      ),
      value: description,
    },
    {
      title: (
        <Typography translateKey="product_variant" variant="body1-semi-bold" />
      ),
      value: variantDescription,
    },
  ];

  const returnDetails = [
    {
      key: 'no_of_returns',
      title: (
        <Typography translateKey="no_of_returns" variant="body1-semi-bold" />
      ),
      value: returnData.amount,
    },
    {
      key: 'return_reason',
      title: (
        <Typography translateKey="return_reason" variant="body1-semi-bold" />
      ),
      value: `${returnData.readableReason} (${returnData.reason})`,
    },
    {
      key: 'comment',
      title: <Typography translateKey="comment" variant="body1-semi-bold" />,
      value: returnData.returnDescription,
    },
  ];

  const alignLeft = ['xs', 'sm', 'md'].includes(screenClass)
    ? 'start'
    : 'center';

  const alignEditButton = ['xs', 'sm', 'md'].includes(screenClass)
    ? 'start'
    : 'end';

  return (
    <BorderRow radius={cornerRadius} fluid>
      <Spacing spacing={3} />
      <Row>
        <Col xs={12} sm={12} md={12} lg="content" xl="content">
          <Row nogutter justify={alignLeft}>
            <ProductImage src={imageUrl} />
          </Row>

          <Spacing spacing={3} />
        </Col>
        <Col>
          <Row>
            {table.map(({ title, value }, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <Col key={index} md={6} lg={12} xl="content">
                <Row nogutter>
                  <TableItem title={title} value={value === null ? undefined : value} />
                </Row>
                <Visible xs sm md lg>
                  <Spacing spacing={3} />
                </Visible>
              </Col>
            ))}
          </Row>
        </Col>
      </Row>

      {!open && (
        <StyledContainer radius={cornerRadius}>
          <Spacing spacing={3} />
          <Row align="start">
            {returnDetails.map(({ title, value, key }) => (
              <Col key={key} xs={12} md="content" lg="content">
                <TableItem title={title} value={value} />
                <Visible xs sm md>
                  <Spacing spacing={3} />
                </Visible>
              </Col>
            ))}
            <Col>
              <Row nogutter justify={alignEditButton}>
                <TeritaryButton
                  translateKey="edit"
                  onClick={() => setOpen(!open)}
                />
              </Row>
            </Col>
          </Row>
          <Spacing spacing={3} />
        </StyledContainer>
      )}
      {open && (
        <FormBorder radius={cornerRadius}>
          <Spacing spacing={3} />
          <Row align="center" nogutter>
            <Col sm="content">
              <Typography translateKey="edit_return" variant="h5" />
            </Col>
          </Row>
          <Spacing spacing={3} />
          <Row>
            <Col>
              <OrderLineForm
                {...props}
                layout="returnLine"
                setOpen={() => setOpen(!open)}
              />
              <Spacing spacing={3} />
            </Col>
          </Row>
        </FormBorder>
      )}
      <Spacing spacing={3} />

      <Container>
        <Row>
          <IconButton
            icon="trash"
            iconColor={theme.colors.red[80]}
            backgroundColor={theme.colors.grey[30]}
            labelRight="delete_returns"
            onClick={() => removeFromReturns(uid, returnData.reason)}
          />
        </Row>
      </Container>
      <Spacing spacing={3} />
    </BorderRow>
  );
};

export default ReturnListItem;
